import React, { useState, useEffect } from 'react';
import { Dialog } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useBookingDetails } from '../../../contexts/BookingDetailsContext';
import { numberToWords } from '../../../utils/helper';
import { fmt } from '../bms_helper';

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  bg:      '#f2f6f3',
  white:   '#ffffff',
  ink:     '#0c1810',
  ink2:    '#1e3328',
  subtle:  '#4d6b58',
  muted:   '#8ba898',
  border:  '#dae6df',
  borderL: '#eaf2ec',
  green:   '#0a6b3e',
  greenMd: '#0d8a50',
  greenLt: '#e4f5ec',
  red:     '#dc2626',
  redLt:   '#fee2e2',
  redBdr:  '#fca5a5',
  amber:   '#d97706',
  amberLt: '#fef3c7',
  amberBdr:'#fcd34d',
};

// ─── Field wrapper ────────────────────────────────────────────────────────────
function Field({ label, required, children, helper, error }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: T.subtle,
        letterSpacing: '0.2px', display: 'flex', alignItems: 'center', gap: 4,
      }}>
        {label}
        {required && <span style={{ color: T.red }}>*</span>}
      </div>
      {children}
      {error  && <div style={{ fontSize: 11, color: T.red,  fontWeight: 600, marginTop: 1 }}>{error}</div>}
      {helper && !error && <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{helper}</div>}
    </div>
  );
}

// ─── Focused input ────────────────────────────────────────────────────────────
function Input({ name, value, onChange, type = 'text', placeholder, min, max, step, suffix, hasError }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        name={name} value={value} onChange={onChange}
        type={type} placeholder={placeholder}
        min={min} max={max} step={step}
        onFocus={() => setFocused(true)}
        onBlur={()  => setFocused(false)}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: suffix ? '9px 46px 9px 12px' : '9px 12px',
          fontSize: 13, color: T.ink,
          background: T.white,
          border: `1.5px solid ${hasError ? T.red : focused ? T.green : T.border}`,
          borderRadius: 9, outline: 'none',
          transition: 'border-color 150ms, box-shadow 150ms',
          boxShadow: hasError
            ? '0 0 0 3px rgba(220,38,38,0.08)'
            : focused ? '0 0 0 3px rgba(10,107,62,0.1)' : 'none',
          fontFamily: 'inherit',
          MozAppearance: 'textfield',
        }}
      />
      {suffix && (
        <span style={{
          position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
          fontSize: 11, fontWeight: 700, color: hasError ? T.red : T.green, pointerEvents: 'none',
        }}>{suffix}</span>
      )}
    </div>
  );
}

// ─── Select ───────────────────────────────────────────────────────────────────
function Select({ name, value, onChange, options = [], placeholder }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <select
        name={name} value={value} onChange={onChange}
        onFocus={() => setFocused(true)}
        onBlur={()  => setFocused(false)}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '9px 34px 9px 12px',
          fontSize: 13, color: value ? T.ink : T.muted,
          background: T.white,
          border: `1.5px solid ${focused ? T.green : T.border}`,
          borderRadius: 9, outline: 'none', appearance: 'none',
          cursor: 'pointer',
          transition: 'border-color 150ms, box-shadow 150ms',
          boxShadow: focused ? '0 0 0 3px rgba(10,107,62,0.1)' : 'none',
          fontFamily: 'inherit',
        }}
      >
        <option value="" disabled>{placeholder || 'Select…'}</option>
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
      <svg style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
        width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.muted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9"/>
      </svg>
    </div>
  );
}

// ─── Section card ─────────────────────────────────────────────────────────────
function Section({ icon, title, children }) {
  return (
    <div style={{
      background: T.white, border: `1px solid ${T.border}`,
      borderRadius: 12, padding: '14px 16px',
      display: 'flex', flexDirection: 'column', gap: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 15 }}>{icon}</span>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: T.ink, letterSpacing: '-0.2px' }}>{title}</span>
      </div>
      {children}
    </div>
  );
}

// ─── Summary stat card ────────────────────────────────────────────────────────
function StatCard({ label, value, highlight, warn }) {
  const bg    = highlight ? T.green : warn ? T.amberLt : T.greenLt;
  const bdr   = highlight ? T.green : warn ? T.amberBdr : T.border;
  const lColor = highlight ? 'rgba(255,255,255,0.75)' : T.subtle;
  const vColor = highlight ? T.white : warn ? T.amber : T.ink2;
  return (
    <div style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 10, color: lColor, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: vColor, letterSpacing: '-0.2px' }}>{value}</div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const AddCommissionPaymentDialog = ({ open, onClose, booking, commission, paymentModes, paymentStatuses }) => {
  const { addBookingCommissionPayment } = useBookingDetails();

  const initialFormState = {
    amount: '', payment_method: '',
    payment_date: dayjs(), reference_number: '',
    notes: '',
  };

  const [formData, setFormData] = useState(initialFormState);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  useEffect(() => {
    if (open) { setFormData(initialFormState); setError(null); }
  }, [open]);

  const cb = booking?.commission_breakdown || {};
  const pendingBalance = parseFloat(cb.total_commission_pending) || 0;
  const amountNum      = parseFloat(formData.amount) || 0;
  const isOverBalance  = amountNum > pendingBalance;

  const handleChange     = (e) => { const { name, value } = e.target; setFormData(p => ({ ...p, [name]: value })); };
  const handleDateChange = (d)  => setFormData(p => ({ ...p, payment_date: d }));

  const validateForm = () => {
    if (!formData.amount)           return 'Amount is required';
    if (amountNum <= 0)             return 'Amount must be greater than 0';
    if (isOverBalance)              return `Amount cannot exceed remaining balance of ₹${fmt(pendingBalance)}`;
    if (!formData.payment_method)   return 'Payment method is required';
    if (!formData.payment_date)     return 'Payment date is required';
    if (formData.payment_method !== 'cash' && !formData.reference_number)
      return 'Reference number is required for non-cash payments';
    return null;
  };

  const handleSubmit = async () => {
    const err = validateForm();
    if (err) { setError(err); return; }
    setLoading(true); setError(null);
    try {
      await addBookingCommissionPayment(booking.id, {
        ...formData,
        payment_date: formData.payment_date.toISOString().split('T')[0],
        amount: parseFloat(formData.amount).toFixed(2),
        payment_status: 'completed', // Always received when adding a payment
      });
      setFormData(initialFormState);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = formData.amount && formData.payment_method && !loading && !isOverBalance;

  return (
    <>
      <style>{`
        @keyframes acpSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .acp-body { animation: acpSlideUp 220ms ease both; }
        .acp-body::-webkit-scrollbar       { width: 5px; }
        .acp-body::-webkit-scrollbar-thumb { background: #ccddd5; border-radius: 99px; }
        .acp-body::-webkit-scrollbar-track { background: transparent; }
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        .acp-textarea:focus { outline: none; border-color: ${T.green} !important; box-shadow: 0 0 0 3px rgba(10,107,62,0.1) !important; }
      `}</style>

      <Dialog
        open={open} onClose={onClose} maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: '16px', overflow: 'hidden', m: 0,
            width: '100%', maxWidth: 520, maxHeight: '92vh',
            boxShadow: '0 24px 80px rgba(0,0,0,0.26)',
            border: `1.5px solid ${T.border}`,
          }
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', height: '92vh', maxHeight: '92vh' }}>

          {/* ── Header ───────────────────────────────────────────────── */}
          <div style={{
            background: 'linear-gradient(135deg,#0a6b3e,#0d8a50)',
            padding: '18px 22px', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
              <div style={{
                width: 42, height: 42, borderRadius: 11, flexShrink: 0,
                background: 'rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
              }}>🏦</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', letterSpacing: '-0.3px' }}>
                  Add Commission Payment
                </div>
                {booking && (
                  <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>
                    Recording payout for booking {booking.booking_id}
                  </div>
                )}
              </div>
            </div>
            <button onClick={onClose} style={{
              background: 'rgba(255,255,255,0.14)', border: 'none',
              borderRadius: 8, width: 32, height: 32, fontSize: 18,
              color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>×</button>
          </div>

          {/* ── Commission summary strip ──────────────────────────────── */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            borderBottom: `1px solid ${T.border}`, flexShrink: 0,
          }}>
            {[
              { label: 'Total',    value: `₹${fmt(cb.total_commission)}` },
              { label: 'Received', value: `₹${fmt(cb.total_commission_received)}` },
              { label: 'Pending',  value: `₹${fmt(cb.total_commission_pending)}`, warn: pendingBalance > 0 },
            ].map((s, i, arr) => (
              <div key={s.label} style={{
                padding: '10px 14px',
                borderRight: i < arr.length - 1 ? `1px solid ${T.border}` : 'none',
                background: s.warn ? T.amberLt : T.white,
              }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{s.label}</div>
                <div style={{ fontSize: 13, fontWeight: 800, color: s.warn ? T.amber : T.ink, marginTop: 2 }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* ── Scrollable body ──────────────────────────────────────── */}
          <div
            className="acp-body"
            style={{
              flex: 1, minHeight: 0, overflowY: 'auto',
              padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10,
              background: T.bg,
            }}
          >
            {/* Error */}
            {error && (
              <div style={{
                background: T.redLt, border: `1.5px solid ${T.redBdr}`,
                borderRadius: 10, padding: '10px 14px',
                fontSize: 12.5, color: T.red, fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span>⚠️</span> {error}
              </div>
            )}

            {/* 1 — Amount */}
            <Section icon="₹" title="Payment Amount">
              <Field
                label="Amount" required
                error={isOverBalance ? `Cannot exceed pending balance of ₹${fmt(pendingBalance)}` : undefined}
                helper={formData.amount && !isOverBalance
                  ? `${numberToWords(parseInt(formData.amount))} rupees only`
                  : undefined}
              >
                <Input
                  name="amount" value={formData.amount} onChange={handleChange}
                  type="number" placeholder="Enter amount"
                  min={0} max={pendingBalance} step={100}
                  suffix="₹" hasError={isOverBalance}
                />
              </Field>
            </Section>

            {/* 2 — Payment Details */}
            <Section icon="💳" title="Payment Details">
              <Field label="Payment Method" required>
                <Select
                  name="payment_method" value={formData.payment_method}
                  onChange={handleChange} options={paymentModes || []}
                  placeholder="Select method"
                />
              </Field>
              <Field label="Payment Date" required>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={formData.payment_date}
                    onChange={handleDateChange}
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: {
                          width: '100%',
                          '& .MuiInputBase-root': { borderRadius: '9px', fontSize: '13px', background: T.white, fontFamily: 'inherit' },
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: T.border },
                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.green, borderWidth: '1.5px' },
                        }
                      }
                    }}
                  />
                </LocalizationProvider>
              </Field>
            </Section>

            {/* 3 — Reference */}
            <Section icon="🔗" title="Reference Number">
              <Field
                label="Reference Number"
                required={formData.payment_method && formData.payment_method !== 'cash'}
                helper={
                  formData.payment_method === 'cash'
                    ? 'Optional for cash payments'
                    : formData.payment_method
                    ? 'Required for non-cash payments'
                    : undefined
                }
              >
                <Input
                  name="reference_number" value={formData.reference_number}
                  onChange={handleChange} placeholder="e.g. UTR123456789"
                />
              </Field>
            </Section>

            {/* 4 — Notes */}
            <Section icon="📝" title="Notes">
              <Field label="Additional Notes">
                <textarea
                  name="notes" value={formData.notes}
                  onChange={handleChange}
                  placeholder="Any notes about this commission payment…"
                  rows={3}
                  className="acp-textarea"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    padding: '10px 12px', fontSize: 13, color: T.ink,
                    background: T.white, border: `1.5px solid ${T.border}`,
                    borderRadius: 9, resize: 'vertical', lineHeight: 1.65,
                    transition: 'border-color 150ms', fontFamily: 'inherit',
                  }}
                />
              </Field>
            </Section>
          </div>

          {/* ── Footer ───────────────────────────────────────────────── */}
          <div style={{
            padding: '13px 18px', flexShrink: 0,
            borderTop: `1.5px solid ${T.border}`, background: '#f8fbf9',
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10,
          }}>
            <button
              onClick={onClose} disabled={loading}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: T.redLt, border: `1.5px solid ${T.redBdr}`,
                borderRadius: 9, padding: '8px 16px',
                fontSize: 12.5, fontWeight: 700, color: T.red,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.6 : 1, fontFamily: 'inherit',
              }}
            >✕ Cancel</button>

            <button
              onClick={handleSubmit} disabled={!canSubmit}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: !canSubmit
                  ? '#7ab898'
                  : 'linear-gradient(135deg,#0a6b3e,#0d8a50)',
                border: 'none', borderRadius: 9, padding: '8px 20px',
                fontSize: 12.5, fontWeight: 700, color: '#fff',
                cursor: !canSubmit ? 'not-allowed' : 'pointer',
                boxShadow: !canSubmit ? 'none' : '0 2px 10px rgba(10,107,62,0.28)',
                transition: 'all 150ms', fontFamily: 'inherit',
              }}
            >
              {loading ? '⏳ Saving…' : '💾 Add Payment'}
            </button>
          </div>

        </div>
      </Dialog>
    </>
  );
};

export default AddCommissionPaymentDialog;
